import React, { forwardRef, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import AIAvatar from '../AIAvatar';
import { cn } from '../../lib/utils';

/**
 * Right-side sliding chat container. Renders header (title + close/clear),
 * a scrollable messages area (children), and a footer (also children slot).
 *
 * Layout is intentionally split into `children` and `footer` props so the
 * caller controls what goes inside the scroll region vs pinned at the bottom.
 */
const ChatPanel = forwardRef(function ChatPanel(
  {
    open,
    onClose,
    onClear,
    canClear,
    title,
    subtitle,
    closeLabel,
    clearLabel,
    headerBadge,
    children,
    footer,
    scrollRef,
    testId = 'ai-chat-panel',
  },
  ref
) {
  const { t } = useTranslation();
  const titleId = useId();
  // Escape key closes the panel when it's open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <>
      {open && (
        <div
          data-testid="ai-chat-backdrop"
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden animate-in fade-in duration-300 ease-out"
          aria-hidden="true"
        />
      )}
      <aside
        ref={ref}
        data-testid={testId}
        role="dialog"
        aria-modal={open ? 'true' : 'false'}
        aria-hidden={!open}
        aria-labelledby={titleId}
        className={cn(
          'rp-advisor-panel-slide',
          'fixed z-50 top-0 right-0 h-full w-full sm:w-[420px] md:w-[440px] lg:w-[480px]',
          'bg-white dark:bg-gradient-to-b dark:from-[#0F1520] dark:to-[#0B0E14] border-l border-slate-200 dark:border-slate-800',
          'shadow-2xl dark:shadow-black/60 flex flex-col',
          'transform transition-transform duration-[420ms]',
          'motion-reduce:transition-none',
          open ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <header className="flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-[#0B0E14]/70 backdrop-blur-xl">
          <div className="flex items-center gap-3 min-w-0">
            <AIAvatar size={32} label={title} animate />
            <div className="min-w-0">
              <h3 id={titleId} className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">{title}</h3>
              {subtitle && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {headerBadge}
            {canClear && (
              <Button
                data-testid="ai-chat-clear-button"
                onClick={onClear}
                variant="ghost"
                size="icon"
                aria-label={clearLabel || t('aiAdvisor.clear')}
                title={clearLabel || t('aiAdvisor.clear')}
                className="rp-advisor-focus h-8 w-8 text-slate-500 hover:text-red-600 dark:hover:text-red-400 transition-colors duration-200"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              data-testid="ai-chat-close-button"
              onClick={onClose}
              variant="ghost"
              size="icon"
              aria-label={closeLabel || t('common.close')}
              className="rp-advisor-focus h-8 w-8 text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 transition-colors duration-200"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </header>

        <div
          ref={scrollRef}
          data-testid="ai-chat-messages"
          className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 sm:py-6 space-y-4 sm:space-y-5"
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
        >
          {children}
        </div>

        {footer}
      </aside>
    </>
  );
});

export default ChatPanel;
